import React, { useState } from 'react';
import { MessageSquare, Send, MapPin, Mail, Phone, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { toast } from 'react-hot-toast';

export function Contact() {
  // Estados para los campos del formulario
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Función para manejar el envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validación básica
    if (!name || !email || !message) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }
    
    setLoading(true);
    
    try {
      // Simulamos una petición al servidor
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Limpiamos el formulario
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      
      // Mostramos mensaje de éxito
      toast.success('¡Tu mensaje ha sido enviado! Te contactaremos pronto.');
    } catch (error) {
      console.error('Error al enviar el mensaje:', error);
      toast.error('Ha ocurrido un error al enviar tu mensaje. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Encabezado */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-purple-200 to-indigo-100 mb-4">
            Contacto
          </h1>
          <p className="text-xl text-purple-200/70 max-w-3xl mx-auto">
            ¿Tienes alguna pregunta o sugerencia? Estamos aquí para ayudarte
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
          {/* Formulario de contacto */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40 p-8 border border-purple-500/30 shadow-lg">
            {/* Efectos de fondo */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-purple-500/30 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">
                  Envíanos un mensaje
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-purple-200 mb-2">
                      Nombre*
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 backdrop-blur-md border border-purple-500/30 rounded-lg focus:border-purple-500/50 focus:outline-none text-white placeholder-purple-200/30 transition-colors"
                      placeholder="Tu nombre"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-purple-200 mb-2">
                      Email*
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 backdrop-blur-md border border-purple-500/30 rounded-lg focus:border-purple-500/50 focus:outline-none text-white placeholder-purple-200/30 transition-colors"
                      placeholder="Tu correo electrónico"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-purple-200 mb-2">
                    Asunto
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 backdrop-blur-md border border-purple-500/30 rounded-lg focus:border-purple-500/50 focus:outline-none text-white placeholder-purple-200/30 transition-colors"
                    placeholder="Asunto de tu mensaje"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-purple-200 mb-2">
                    Mensaje*
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 bg-white/5 backdrop-blur-md border border-purple-500/30 rounded-lg focus:border-purple-500/50 focus:outline-none text-white placeholder-purple-200/30 transition-colors resize-none"
                    placeholder="Escribe tu mensaje aquí"
                    required
                  />
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Enviar mensaje
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Información de contacto */}
          <div className="space-y-8">
            {/* Mapa */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40 p-8 border border-purple-500/30 shadow-lg h-80">
              {/* Efectos de fondo */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
              
              {/* Aquí iría el mapa real con alguna biblioteca como Google Maps o Leaflet */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-purple-200/50">
                  <MapPin className="w-12 h-12 mx-auto mb-4" />
                  <p>Mapa no disponible en la vista previa</p>
                </div>
              </div>
            </div>
            
            {/* Detalles de contacto */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/40 p-8 border border-purple-500/30 shadow-lg">
              {/* Efectos de fondo */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/10 rounded-full blur-xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-purple-500/30 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">
                    Información de contacto
                  </h2>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-white/5 border border-purple-500/20 flex-shrink-0">
                      <MapPin className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-1">Dirección</h3>
                      <p className="text-purple-200/70">
                        Av. Libertador 1234, Piso 5<br />
                        Santiago, Chile
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-white/5 border border-purple-500/20 flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-1">Email</h3>
                      <p className="text-purple-200/70">
                        <a href="mailto:contacto@curvia.ai" className="hover:text-purple-300 transition-colors">
                          contacto@curvia.ai
                        </a><br />
                        <a href="mailto:soporte@curvia.ai" className="hover:text-purple-300 transition-colors">
                          soporte@curvia.ai
                        </a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-white/5 border border-purple-500/20 flex-shrink-0">
                      <Phone className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-1">Teléfono</h3>
                      <p className="text-purple-200/70">
                        <a href="tel:+5622234567" className="hover:text-purple-300 transition-colors">
                          +56 2 223 4567
                        </a><br />
                        <a href="tel:+56912345678" className="hover:text-purple-300 transition-colors">
                          +56 9 1234 5678
                        </a>
                      </p>
                    </div>
                  </div>
                  
                  {/* Redes sociales */}
                  <div className="pt-4 border-t border-purple-500/20">
                    <h3 className="text-lg font-medium text-white mb-3">Síguenos</h3>
                    <div className="flex gap-4">
                      <a href="#" className="p-3 rounded-full bg-white/5 border border-purple-500/20 hover:bg-white/10 hover:border-purple-500/40 transition-all" aria-label="Facebook">
                        <Facebook className="w-5 h-5 text-blue-400" />
                      </a>
                      <a href="#" className="p-3 rounded-full bg-white/5 border border-purple-500/20 hover:bg-white/10 hover:border-purple-500/40 transition-all" aria-label="Instagram">
                        <Instagram className="w-5 h-5 text-pink-400" />
                      </a>
                      <a href="#" className="p-3 rounded-full bg-white/5 border border-purple-500/20 hover:bg-white/10 hover:border-purple-500/40 transition-all" aria-label="Twitter">
                        <Twitter className="w-5 h-5 text-blue-400" />
                      </a>
                      <a href="#" className="p-3 rounded-full bg-white/5 border border-purple-500/20 hover:bg-white/10 hover:border-purple-500/40 transition-all" aria-label="LinkedIn">
                        <Linkedin className="w-5 h-5 text-blue-500" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* FAQ */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200 mb-8 text-center">
            Preguntas Frecuentes
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              {
                question: "¿Cuánto tiempo toma generar un CV?",
                answer: "Nuestro sistema de IA puede generar un CV optimizado en menos de 60 segundos una vez que hayas proporcionado toda la información necesaria."
              },
              {
                question: "¿Cómo funciona el sistema de monedas?",
                answer: "Cada vez que generas un CV optimizado con IA, se descuentan 100 monedas de tu cuenta. Puedes adquirir monedas a través de nuestros diferentes paquetes disponibles."
              },
              {
                question: "¿Puedo editar mi CV después de generarlo?",
                answer: "Sí, tienes acceso completo para editar todos los aspectos de tu CV después de la generación, incluyendo contenido, diseño y colores."
              },
              {
                question: "¿En qué formatos puedo exportar mi CV?",
                answer: "Puedes exportar tu CV en formato PDF, Word (DOC/DOCX) y TXT. Los usuarios premium también tienen acceso a formatos adicionales."
              },
              {
                question: "¿Mis datos están seguros?",
                answer: "Absolutamente. Utilizamos cifrado de extremo a extremo y cumplimos con todas las normativas de protección de datos. Tu información nunca se comparte con terceros."
              },
              {
                question: "¿Ofrecen soporte personalizado?",
                answer: "Sí, los usuarios con el plan Premium tienen acceso a soporte personalizado para optimizar sus CVs con la ayuda de nuestros expertos en reclutamiento."
              }
            ].map((faq, index) => (
              <div 
                key={index} 
                className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-900/30 via-indigo-900/30 to-purple-900/30 p-6 border border-purple-500/20 shadow-lg transition-all duration-300 hover:border-purple-500/30"
              >
                {/* Efectos de fondo */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/5 rounded-full blur-xl"></div>
                <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-blue-500/5 rounded-full blur-xl"></div>
                
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-white mb-3">{faq.question}</h3>
                  <p className="text-purple-200/70 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* CTA */}
        <div className="text-center">
          <div className="max-w-3xl mx-auto p-8 rounded-2xl relative overflow-hidden">
            {/* Efectos de fondo */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-indigo-600/20 rounded-2xl"></div>
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-purple-500/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/10 rounded-full blur-xl"></div>
            
            {/* Borde brillante */}
            <div className="absolute inset-0 rounded-2xl border border-purple-500/30"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-purple-200 to-indigo-100 mb-4">
                ¿Necesitas ayuda específica?
              </h2>
              <p className="text-lg text-purple-200/80 mb-6 max-w-2xl mx-auto">
                Nuestro equipo de soporte está disponible para ayudarte con cualquier duda o problema. No dudes en contactarnos.
              </p>
              
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 rounded-full border border-purple-500/30">
                <Mail className="w-4 h-4 text-purple-400" />
                <span className="text-white">soporte@curvia.ai</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact; 